import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const MARKETPLACES = ["Amazon.com", "Amazon.in", "Amazon.co.uk", "Amazon.de", "Amazon.ca", "Amazon.com.au", "Other"];
const REV_BANDS = ["< $1K/mo", "$1K – $10K/mo", "$10K – $50K/mo", "$50K – $250K/mo", "$250K+/mo"];

function Label({ children }) { return <div className="label-mono mb-1.5">{children}</div>; }

export default function WaitlistDialog({ trigger, sourcePage = "landing" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    email: user?.email || "",
    name: user?.name || "",
    business_name: user?.business_name || "",
    marketplace: user?.marketplace || "",
    monthly_revenue_band: user?.monthly_revenue_band || "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/waitlist", form);
      setResult(data);
      toast.success(data.duplicate ? "Already on the list — see your position below." : "You're on the waitlist.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button data-testid="waitlist-cta">Join the waitlist →</Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md rounded-none border-ink no-round" data-testid="waitlist-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tightest text-2xl">Join the waitlist.</DialogTitle>
          <DialogDescription className="text-ink/60">
            Priority access, launch discounts, and higher tool limits.
          </DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="py-8" data-testid="waitlist-result">
            <div className="label-mono mb-3">Your position</div>
            <div className="font-mono font-medium text-6xl tabular text-ink tracking-tightest">
              #{(result.position ?? 0).toLocaleString()}
            </div>
            <p className="text-ink/60 mt-4 text-sm">You're in line. We'll email you the moment your slot opens.</p>
            <Button className="mt-6 w-full" variant="outline" onClick={() => setOpen(false)} data-testid="waitlist-close">
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Name</Label>
                <Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="waitlist-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="waitlist-email" />
              </div>
            </div>
            <div>
              <Label>Business (optional)</Label>
              <Input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="waitlist-biz" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Marketplace</Label>
                <Select value={form.marketplace} onValueChange={(v) => set("marketplace", v)}>
                  <SelectTrigger data-testid="waitlist-marketplace" className="rounded-none border-rule h-10"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="rounded-none border-rule">{MARKETPLACES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Monthly revenue</Label>
                <Select value={form.monthly_revenue_band} onValueChange={(v) => set("monthly_revenue_band", v)}>
                  <SelectTrigger data-testid="waitlist-revenue" className="rounded-none border-rule h-10"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="rounded-none border-rule">{REV_BANDS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={submitting} className="w-full" data-testid="waitlist-submit">
                {submitting ? "Joining…" : "Join the waitlist"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
